import React, { useState, useRef, useCallback, useEffect } from 'react'
import { X, Users, Upload, Image as ImageIcon, AlertCircle } from 'lucide-react'
import { imageUploadService, ImageUploadResult } from '../../services/imageUploadService'
import { FILL_GRADIENT, LINE_GRADIENT } from '../../constants/gradients'
import { analyticsService } from '../../services/analyticsService'

interface CreateTeamModalProps {
  onClose: () => void
  onCreateTeam: (teamData: any) => void
}

export const CreateTeamModal: React.FC<CreateTeamModalProps> = ({
  onClose,
  onCreateTeam
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    slug: '',
    avatarUrl: '',
    type: 'collaborative' as 'collaborative' | 'organization'
  })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(false)
  const [avatarUploading, setAvatarUploading] = useState(false)
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  
  // Track modal opened
  useEffect(() => {
    analyticsService.track({
      name: 'modal_opened',
      properties: {
        modal_type: 'create_team'
      }
    });
  }, [])

  const validateForm = () => {
    const newErrors: Record<string, string> = {}
    
    if (!formData.name.trim()) {
      newErrors.name = 'Team name is required'
    } else if (formData.name.length < 3) {
      newErrors.name = 'Team name must be at least 3 characters'
    }
    
    if (!formData.slug.trim()) {
      newErrors.slug = 'Team slug is required'
    } else if (!/^[a-z0-9-]+$/.test(formData.slug)) {
      newErrors.slug = 'Slug can only contain lowercase letters, numbers, and hyphens'
    }
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) return
    
    setLoading(true)
    try {
      await onCreateTeam({
        ...formData,
        isPublic: false // Default to private teams
      })
      onClose()
    } catch (error) {
      console.error('Failed to create team:', error)
      setErrors({ general: 'Failed to create team. Please try again.' })
    } finally {
      setLoading(false)
    }
  }

  const handleNameChange = (name: string) => {
    const slug = name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
    
    setFormData(prev => ({ ...prev, name, slug }))
  }

  const handleAvatarUpload = useCallback(async (file: File) => {
    let preview: string | null = null
    
    try {
      setAvatarUploading(true)
      setErrors(prev => ({ ...prev, avatar: '' }))
      
      // Generate temporary team ID for upload path
      const tempTeamId = `temp-${Date.now()}`
      
      // Generate and show preview while uploading
      preview = imageUploadService.generatePreviewUrl(file)
      setAvatarPreview(preview)
      
      // Upload file (includes moderation)
      const result = await imageUploadService.uploadImage(
        file,
        tempTeamId,
        'avatar'
      )
      
      // Clean up preview URL
      imageUploadService.cleanupPreviewUrl(preview)
      
      // Update form data with avatar URL (moderation passed)
      setFormData(prev => ({ ...prev, avatarUrl: result.url }))
      
      console.log('✅ Avatar uploaded successfully:', result)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Avatar upload failed'
      setErrors(prev => ({ ...prev, avatar: errorMessage }))
      console.error('❌ Avatar upload failed:', err)
      
      // REMOVE preview if moderation failed
      if (preview) {
        imageUploadService.cleanupPreviewUrl(preview)
      }
      setAvatarPreview(null)
    } finally {
      setAvatarUploading(false)
    }
  }, [])

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (files && files.length > 0) {
      handleAvatarUpload(files[0])
    }
  }, [handleAvatarUpload])

  const openFileDialog = () => {
    if (!avatarUploading && fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  const removeAvatar = () => {
    if (avatarPreview) {
      imageUploadService.cleanupPreviewUrl(avatarPreview)
      setAvatarPreview(null)
    }
    setFormData(prev => ({ ...prev, avatarUrl: '' }))
  }

  const displayAvatarUrl = avatarPreview || formData.avatarUrl
  const hasAvatar = !!displayAvatarUrl

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-dark-surface rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col">
        {/* Gradient Line at Top */}
        <div className="h-1 w-full" style={{ background: FILL_GRADIENT }} />
        
        {/* Header */}
        <div className="flex items-center justify-between p-6 pb-4">
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-dark-text">
            Create New Team
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
          >
            <X className="w-6 h-6 text-gray-600 dark:text-gray-400" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 pb-6 space-y-4 overflow-y-auto flex-1">
          {/* General Error */}
          {errors.general && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center">
              <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
              <p className="text-sm text-red-700 dark:text-red-200">{errors.general}</p>
            </div>
          )}

          {/* Team Name */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-dark-text mb-2">
              Team Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleNameChange(e.target.value)}
              placeholder="Enter team name"
              className={`w-full px-4 py-2.5 border-2 rounded-xl transition-colors focus:outline-none dark:bg-dark-bg dark:text-dark-text ${
                errors.name 
                  ? 'border-red-500 focus:border-red-500' 
                  : 'border-gray-200 dark:border-dark-border focus:border-gray-300 dark:focus:border-gray-600'
              }`}
            />
            {errors.name && (
              <p className="text-sm text-red-500 mt-1.5">{errors.name}</p>
            )}
          </div>

          {/* Team Slug */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-dark-text mb-2">
              Team Slug *
            </label>
            <input
              type="text"
              value={formData.slug}
              onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
              placeholder="team-slug"
              className={`w-full px-4 py-2.5 border-2 rounded-xl transition-colors focus:outline-none dark:bg-dark-bg dark:text-dark-text ${
                errors.slug 
                  ? 'border-red-500 focus:border-red-500' 
                  : 'border-gray-200 dark:border-dark-border focus:border-gray-300 dark:focus:border-gray-600'
              }`}
            />
            <p className="text-xs text-gray-400 dark:text-dark-text-muted mt-1.5">
              Used in URLs and mentions (e.g., @{formData.slug || 'team-slug'})
            </p>
            {errors.slug && (
              <p className="text-sm text-red-500 mt-1.5">{errors.slug}</p>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-dark-text mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="What's this team about?"
              rows={3}
              className="w-full px-4 py-2.5 border-2 border-gray-200 dark:border-dark-border rounded-xl transition-colors focus:outline-none focus:border-gray-300 dark:focus:border-gray-600 dark:bg-dark-bg dark:text-dark-text resize-none"
            />
          </div>

          {/* Team Avatar */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-dark-text mb-2">
              Team Avatar
              <span className="text-gray-400 dark:text-gray-500 ml-1 font-normal">(Max 2MB)</span>
            </label>
            
            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleFileInputChange}
              className="hidden"
              disabled={avatarUploading}
            />
            
            <div className="flex items-center space-x-4">
              {/* Avatar Preview */}
              <div className="relative">
                {hasAvatar ? (
                  <div className="relative w-16 h-16 rounded-full overflow-hidden">
                    <img
                      src={displayAvatarUrl}
                      alt="Team Avatar"
                      className="w-full h-full object-cover"
                    />
                    {avatarUploading && (
                      <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                        <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="w-16 h-16 bg-gray-200 dark:bg-dark-hover rounded-full flex items-center justify-center">
                    <Users className="w-6 h-6 text-gray-400" />
                  </div>
                )}
              </div>
              
              {/* Upload Controls */}
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={openFileDialog}
                  className="px-4 py-2 text-sm font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-all duration-200 hover:scale-105 active:scale-95 flex items-center gap-2"
                  disabled={avatarUploading}
                >
                  {avatarUploading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      Upload
                    </>
                  )}
                </button>
                {hasAvatar && !avatarUploading && (
                  <button
                    type="button"
                    onClick={removeAvatar}
                    className="px-4 py-2 text-sm font-semibold text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all duration-200 hover:scale-105 active:scale-95 flex items-center gap-2"
                    disabled={avatarUploading}
                  >
                    <X className="w-4 h-4" />
                    Remove
                  </button>
                )}
              </div>
            </div>
            
            {/* Avatar Error */}
            {errors.avatar && (
              <p className="text-sm text-red-500 mt-1">{errors.avatar}</p>
            )}
            
            {/* Avatar Help Text */}
            <p className="text-xs text-gray-400 dark:text-dark-text-muted mt-1.5">
              Upload a square image for best results. Supported formats: JPEG, PNG, WebP
            </p>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-6 border-t border-gray-200 dark:border-dark-border">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2.5 text-sm font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-all duration-200 hover:scale-105 active:scale-95"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2.5 text-sm font-semibold text-white rounded-lg transition-all duration-200 hover:shadow-lg hover:scale-105 active:scale-95 flex items-center gap-2"
              style={{ background: FILL_GRADIENT }}
              disabled={loading}
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Creating...
                </>
              ) : (
                'Create Team'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
} 